import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { IndianRupee, Coins, Clock, CheckCircle, XCircle, Search, RefreshCw, Loader2, ArrowUpDown, Download } from 'lucide-react';

export const PaymentsTab = ({ api, isDark, theme }) => {
  const { cardBg, cardBorder, innerCardBg } = theme;
  const [creditPayments, setCreditPayments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('payments');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [paymentsRes, transRes] = await Promise.all([
        api.get('/payments/admin/credit-payments'),
        api.get('/payments/admin/all-transactions'),
      ]);
      setCreditPayments(paymentsRes.data);
      setTransactions(transRes.data);
    } catch (err) {
      toast.error('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Stats
  const completedPayments = creditPayments.filter(p => p.status === 'completed');
  const totalRevenue = completedPayments.reduce((sum, p) => sum + (p.total_amount || 0), 0);
  const totalCreditsSold = completedPayments.reduce((sum, p) => sum + (p.credits || 0), 0);
  const pendingCount = creditPayments.filter(p => p.status === 'pending').length;

  // Filtered payments
  const filteredPayments = creditPayments.filter(p => {
    const matchesSearch = !searchTerm ||
      (p.user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.user_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.organization_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.razorpay_payment_id || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Filtered transactions
  const filteredTransactions = transactions.filter(t => {
    const matchesSearch = !searchTerm ||
      (t.user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.user_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || t.transaction_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed': return <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30 border">Completed</Badge>;
      case 'pending': return <Badge className="bg-amber-500/20 text-amber-500 border-amber-500/30 border">Pending</Badge>;
      case 'failed': return <Badge className="bg-red-500/20 text-red-500 border-red-500/30 border">Failed</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'purchase': return <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30 border">Purchase</Badge>;
      case 'deduction':
      case 'debit': return <Badge className="bg-red-500/20 text-red-500 border-red-500/30 border">Debit</Badge>;
      case 'allocation':
      case 'credit': return <Badge className="bg-blue-500/20 text-blue-500 border-blue-500/30 border">Credit</Badge>;
      case 'adjustment': return <Badge className="bg-purple-500/20 text-purple-500 border-purple-500/30 border">Adjustment</Badge>;
      case 'refund': return <Badge className="bg-amber-500/20 text-amber-500 border-amber-500/30 border">Refund</Badge>;
      default: return <Badge variant="secondary">{type}</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-payments-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className={`${cardBg} ${cardBorder}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Revenue</p>
                <p className="text-xl font-bold" data-testid="total-revenue">{formatCurrency(totalRevenue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`${cardBg} ${cardBorder}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Coins className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Credits Sold</p>
                <p className="text-xl font-bold" data-testid="total-credits-sold">{totalCreditsSold.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`${cardBg} ${cardBorder}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pending</p>
                <p className="text-xl font-bold" data-testid="pending-count">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`${cardBg} ${cardBorder}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Payments</p>
                <p className="text-xl font-bold" data-testid="total-payments">{creditPayments.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Toggle between views */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex gap-1 p-1 rounded-lg bg-muted/50">
          <button
            onClick={() => { setActiveView('payments'); setSearchTerm(''); setStatusFilter('all'); }}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${activeView === 'payments' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="view-payments-btn"
          >
            Credit Payments
          </button>
          <button
            onClick={() => { setActiveView('transactions'); setSearchTerm(''); setTypeFilter('all'); }}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${activeView === 'transactions' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="view-transactions-btn"
          >
            All Transactions
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search name, email, payment ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-64"
              data-testid="payments-search"
            />
          </div>
          {activeView === 'payments' && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          )}
          {activeView === 'transactions' && (
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-36" data-testid="type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="purchase">Purchase</SelectItem>
                <SelectItem value="deduction">Deduction</SelectItem>
                <SelectItem value="allocation">Allocation</SelectItem>
                <SelectItem value="adjustment">Adjustment</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={fetchData} className={cardBorder} data-testid="refresh-payments">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Credit Payments Table */}
      {activeView === 'payments' && (
        <Card className={`${cardBg} ${cardBorder}`}>
          <CardContent className="p-0">
            {filteredPayments.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <IndianRupee className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>No credit payments found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="payments-table">
                  <thead>
                    <tr className={`border-b ${cardBorder} text-left`}>
                      <th className="px-4 py-3 font-medium text-muted-foreground">Client</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground">Organization</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground">Package</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground text-right">Credits</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground text-right">Amount</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground text-right">GST</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground text-right">Total</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground">Payment ID</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground">Status</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.map((p) => (
                      <tr key={p.id} className={`border-b ${cardBorder} hover:${innerCardBg}`} data-testid={`payment-row-${p.id}`}>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium">{p.user_name}</p>
                            <p className="text-xs text-muted-foreground">{p.user_email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{p.organization_name || '—'}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={cardBorder}>{p.tier_label || '—'}</Badge>
                          {p.discount_percent > 0 && (
                            <span className="text-xs text-emerald-500 ml-1">-{p.discount_percent}%</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-medium">{p.credits}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(p.discounted_amount)}</td>
                        <td className="px-4 py-3 text-right text-muted-foreground">{formatCurrency(p.gst_amount)}</td>
                        <td className="px-4 py-3 text-right font-semibold">{formatCurrency(p.total_amount)}</td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs">{p.razorpay_payment_id || '—'}</span>
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(p.status)}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                          {p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                          <br />
                          <span className="text-[10px]">{p.created_at ? new Date(p.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* All Transactions Table */}
      {activeView === 'transactions' && (
        <Card className={`${cardBg} ${cardBorder}`}>
          <CardContent className="p-0">
            {filteredTransactions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Coins className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>No transactions found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="transactions-table">
                  <thead>
                    <tr className={`border-b ${cardBorder} text-left`}>
                      <th className="px-4 py-3 font-medium text-muted-foreground">User</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground">Organization</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground">Type</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground text-right">Credits</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground text-right">Amount (INR)</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground">Description</th>
                      <th className="px-4 py-3 font-medium text-muted-foreground">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map((t) => (
                      <tr key={t.id} className={`border-b ${cardBorder} hover:${innerCardBg}`} data-testid={`transaction-row-${t.id}`}>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium">{t.user_name}</p>
                            <p className="text-xs text-muted-foreground">{t.user_email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{t.organization_name || '—'}</td>
                        <td className="px-4 py-3">{getTypeBadge(t.transaction_type)}</td>
                        <td className="px-4 py-3 text-right font-medium">
                          <span className={t.transaction_type === 'deduction' || t.transaction_type === 'debit' ? 'text-red-500' : 'text-emerald-500'}>
                            {t.amount < 0 ? '' : (t.transaction_type === 'deduction' || t.transaction_type === 'debit' ? '-' : '+')}{t.amount}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">{t.payment_amount ? formatCurrency(t.payment_amount) : '—'}</td>
                        <td className="px-4 py-3 text-xs max-w-[250px] truncate">{t.description || '—'}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                          {t.created_at ? new Date(t.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                          <br />
                          <span className="text-[10px]">{t.created_at ? new Date(t.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
